import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Paper, Button, Backdrop, CircularProgress, Checkbox, Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DateInput from "../components/Inputs/Date";
import Dropdown from "../components/Inputs/Dropdown";
import Toggle from "../components/Inputs/Toggle";
import { BudgetItem, BucketNodeSnapshotResponse, BuildReportColumnLabels, CurrentUser, CustomReportFilter, CustomReportSettings, DateTime, FamilyBucketLabel, FamilyBucketPluralLabel, GetEnabledBucketDefinitions, SelectableItem, TransactionItem, USCurrency, UserItem, SubBudgetItem } from "../types";
import ReportPreviewTables from "../components/Reports/ReportPreviewTables";
import { buildReportPreview } from "../features/Reports/preview";
import { useItems } from "../features/Fetch/Items";
import { DownloadPdf } from "../features/ReportService";
import { ModalProps, setProps } from "../features/Slices/modalSlice";
import { setUseModalSpace } from "../features/Slices/modalSpaceSlice";
import { SavedReport, addSavedReport, updateSavedReport, deleteSavedReport, RelativeDateRange, RelativeDateRangeType } from "../features/Slices/savedReportsSlice";
import dayjs from "dayjs";
import Description from "../components/Inputs/Description";

const CustomReport = (props: any) => {
  const repo = useItems();

  let budgets: BudgetItem[] = useSelector((s: any) => s.budgets.value);
  const bucketSnapshot: BucketNodeSnapshotResponse | undefined = useSelector((s: any) => s.bucketNodes.value);
  let users: UserItem[] = useSelector((s: any) => s.users.value);
  const transactions: TransactionItem[] | null = repo.SortedTransactions(null, true);
  const checkRequests: TransactionItem[] = useSelector((s: any) => s.checkRequests.value);
  const savedReports: SavedReport[] = useSelector((s: any) => s.savedReports.value);
	const user: CurrentUser = useSelector((state: any) => state.userData.value);
  const budgetLabel = FamilyBucketLabel(user, "Budget", "Budget");
  const subBudgetLabel = FamilyBucketLabel(user, "SubBudget", "SubBudget");
  const budgetPluralLabel = FamilyBucketPluralLabel(user, "budget", "Budgets");
  const subBudgetPluralLabel = FamilyBucketPluralLabel(user, "subbudget", "SubBudgets");

  const bucketDefinitionsForReports = useMemo(() => GetEnabledBucketDefinitions(user)
    .filter(d => d.bucketTypeKey?.toLowerCase() !== 'budget'), [user]);
  const bucketColumnKeys = useMemo(() => bucketDefinitionsForReports.map(d => `Bucket:${d.bucketTypeKey}`), [bucketDefinitionsForReports]);
  const bucketNodesByType = useMemo(() => {
    const map: { [key: string]: { id: number, name: string }[] } = {};
    (bucketSnapshot?.nodes ?? []).forEach(node => {
      if (!node.bucketTypeKey || node.isInactive) return;
      const key = node.bucketTypeKey;
      if (!map[key]) map[key] = [];
      map[key].push({ id: node.bucketNodeId, name: node.displayName });
    });
    Object.keys(map).forEach(k => map[k].sort((a, b) => a.name.localeCompare(b.name)));
    return map;
  }, [bucketSnapshot]);

  const dispatch = useDispatch();
  const modal: ModalProps = useSelector((state: any) => state.modal.value);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Controls state
  const [startDate, setStartDate] = useState<DateTime>(new DateTime().startOfMonth());
  const [endDate, setEndDate] = useState<DateTime>(new DateTime());
  const [includeSpending, setIncludeSpending] = useState<boolean>(true);
  const [includeMiles, setIncludeMiles] = useState<boolean>(false);
  const [includeCheckRequests, setIncludeCheckRequests] = useState<boolean>(false);
  const [combineSplitBudgets, setCombineSplitBudgets] = useState<boolean>(false);
  const [includeReceipts, setIncludeReceipts] = useState<boolean>(false);
  const [groupReceipts, setGroupReceipts] = useState<boolean>(false);
  const [selectedBudgets, setSelectedBudgets] = useState<number[]>([]);
  const [selectedSubBudgets, setSelectedSubBudgets] = useState<number[]>([]);
  const [selectedBucketNodeIds, setSelectedBucketNodeIds] = useState<{ [bucketTypeKey: string]: number[] }>({});
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [grouping, setGrouping] = useState<string>("");
  const allColumns = useMemo(() => [
    "Date", "Name", "Merchant", "Budget", "SubBudget",
    ...bucketColumnKeys,
    "Description", "Amount"
  ], [bucketColumnKeys]);
  const [columns, setColumns] = useState<string[]>(["Date", "Merchant", "Description", "Amount"]);
  const [overlay, setOverlay] = useState(false);
  const [reportName, setReportName] = useState<string>("");
  const [reportId, setReportId] = useState<string | null>(null);
  const [dateRangeType, setDateRangeType] = useState<RelativeDateRangeType>('ThisMonth');
  const [dateRangeValue, setDateRangeValue] = useState<number | undefined>(undefined);
  // Column ordering state (tracks all columns order, enabled ones are in `columns`)
  const [columnOrder, setColumnOrder] = useState<string[]>(() => [
    ...columns,
    ...allColumns.filter(c => !columns.includes(c))
  ]);

  let subBudgets: SubBudgetItem[] = budgets.filter(b => selectedBudgets.length == 0 || selectedBudgets.includes(b.budgetId)).flatMap(b => b.subBudgets ?? []);

  // Keep column order in sync as bucket definitions load/change.
  useEffect(() => {
    setColumnOrder(prev => {
      const remaining = prev.filter(c => allColumns.includes(c));
      const additions = allColumns.filter(c => !remaining.includes(c));
      return [...remaining, ...additions];
    });
    setColumns(prev => prev.filter(c => allColumns.includes(c)));
  }, [allColumns]);

  function GetReportLabel(value: string) {
    if (value && value.toLowerCase().startsWith('bucket:')) {
      const typeKey = value.substring('bucket:'.length);
      return FamilyBucketLabel(user, typeKey, typeKey);
    }
    switch (value) {
      case "Budget":
        return budgetLabel;
      case "SubBudget":
        return subBudgetLabel;
      default:
        return value;
    }
  }

  const defaultReportName = useMemo(() => {
    if (grouping && grouping.toLowerCase().startsWith('bucket:')) {
      return `Custom ${GetReportLabel(grouping)} Report`;
    }
    switch (grouping) {
      case "Budget":
        return `Custom ${budgetLabel} Report`;
      case "SubBudget":
        return `Custom ${subBudgetLabel} Report`;
      default:
        return grouping ? `Custom ${grouping} Report` : 'Custom Report';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetLabel, grouping, subBudgetLabel, user]);

  if (!user.isHoH) {
    if (selectedUsers.length === 0 && selectedBudgets.length === 0) {
      users = users?.filter(u => u.isMe);
      setSelectedUsers(users.map(u => u.userAccountId));
    } else if (selectedBudgets.length > 0 && (selectedUsers.length === 0 || selectedUsers.some(su => su !== users.find(u => u.isMe)?.userAccountId))) {
      budgets = budgets?.filter(b => b.isMine);
      if (selectedBudgets.some(sb => !budgets.find(b => b.budgetId === sb))) {
        const newBudgets = budgets.filter(b => selectedBudgets.includes(b.budgetId)).map(b => b.budgetId);
        if (newBudgets.length === 0) {
          newBudgets.push(...budgets.map(b => b.budgetId));
        }
        setSelectedBudgets(newBudgets);
      }
    } else if (selectedUsers.length === 1 && selectedBudgets.length === 0) {
      users = users?.filter(u => u.isMe);
    }
  }

  useEffect(() => {
    dispatch(setProps({ ...modal, IsOpen: false }));
    dispatch(setUseModalSpace(false));
    return () => { dispatch(setUseModalSpace(true)); };
	// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Load a saved report if reportId query param provided
  useEffect(() => {
    const id = searchParams.get('reportId');
    if (!id) return;
    const found = savedReports.find(r => r.id === id);
    if (!found) return;
    const s = found.settings;
    const f = s.filter || {} as CustomReportFilter;
    setReportId(found.id);
    setReportName(found.name);
    // Apply relative range if present
    if (found.relativeRange) {
        setDateRangeType(found.relativeRange.type as RelativeDateRangeType);
        setDateRangeValue(found.relativeRange.value);
    } else if (f.startDate || f.endDate) {
      // Fallback to custom for legacy
      setDateRangeType('Custom');
    } else {
      setDateRangeType('ThisMonth');
    }
    if (f.startDate) setStartDate(new DateTime(f.startDate as any));
    if (f.endDate) setEndDate(new DateTime(f.endDate as any));
    setIncludeSpending(!!f.includeSpending);
    setIncludeMiles(!!f.includeMiles);
    setIncludeCheckRequests(!!f.includeCheckRequests);
    setCombineSplitBudgets(!!f.combineSplitBudgets);
    setIncludeReceipts(!!f.includeReceipts);
    setGroupReceipts(!!f.groupReceipts);
    setSelectedBudgets(f.budgetIds ?? []);
    setSelectedSubBudgets(f.subBudgetIds ?? []);
    setSelectedBucketNodeIds(f.bucketNodeIds ?? {});
    setSelectedUsers(f.createdById ?? []);
    setGrouping((s.grouping as any) ?? "");
    // columns and column order
    const loadedCols = Array.isArray(s.columns) && s.columns.length > 0 ? s.columns : columns;
    setColumns(loadedCols);
    setColumnOrder([
      ...loadedCols,
      ...allColumns.filter(c => !loadedCols.includes(c))
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, savedReports]);

  // Build preview data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const computedRange = useMemo(() => computeDateRange(dateRangeType, dateRangeValue, new DateTime()), [dateRangeType, dateRangeValue]);
  const effectiveStart = dateRangeType === 'Custom' ? startDate : computedRange.start;
  const effectiveEnd = dateRangeType === 'Custom' ? endDate : computedRange.end;

  const { groups: previewGroups, grandTotal } = useMemo(() => buildReportPreview({
    transactions: transactions || [],
    checkRequests: checkRequests || [],
    budgets: budgets || [],
    subBudgets: subBudgets || [],
    users: users || [],
    settings: {
      title: reportName || defaultReportName,
      columns,
      grouping,
      filter: {
        startDate: effectiveStart,
        endDate: effectiveEnd,
        budgetIds: selectedBudgets.length ? selectedBudgets : undefined,
        subBudgetIds: selectedSubBudgets.length ? selectedSubBudgets : undefined,
        bucketNodeIds: Object.keys(selectedBucketNodeIds).length ? selectedBucketNodeIds : undefined,
        createdById: selectedUsers.length ? selectedUsers : undefined,
        includeSpending,
        includeMiles,
        includeCheckRequests,
        combineSplitBudgets,
        includeReceipts,
        groupReceipts
      } as CustomReportFilter
    } as CustomReportSettings
  }), [
    transactions,
    checkRequests,
    budgets,
    subBudgets,
    users,
    reportName,
    effectiveStart,
    effectiveEnd,
    includeMiles,
    includeSpending,
    includeCheckRequests,
    combineSplitBudgets,
    includeReceipts,
    groupReceipts,
    selectedBudgets,
    selectedSubBudgets,
    selectedBucketNodeIds,
    selectedUsers,
    defaultReportName,
    grouping,
    columns
  ]);

  const sensors = useSensors(
    useSensor(PointerSensor)
  );

  return (
  <div className="container-fluid customReport-no-scroll-container">
      <Grid container spacing={2} sx={{ height: '100%' }}>
        <Grid item xs={12} md={7} lg={8} sx={{ height: '100%' }}>
          <Paper sx={{ p: 1.5, mt: 1, height: 'calc(100% - 8px)', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper', pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <h2 className="customReport-title">{reportId ? reportName : 'Custom Report Builder'}</h2>
                <Button variant="outlined" color="secondary" onClick={handleClose}>Close</Button>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button variant="contained" color="primary" onClick={handleDownload}>Download PDF</Button>
                <h3 className="customReport-subtitle m-0">Preview Total: {USCurrency(grandTotal)}</h3>
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <ReportPreviewTables groups={previewGroups} columns={columns} grouping={grouping} />
            </Box>
          </Paper>
        </Grid>

        {/* Right: Settings panel, its own scroll. Buttons stick to bottom */}
        <Grid item xs={12} md={5} lg={4} sx={{ height: '100%' }}>
          <Paper sx={{ p: 1.5, mt: 1, height: 'calc(100% - 8px)', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <Grid container spacing={1}>
                <Description Size={12} Class="mt-3" Label="Report Name" Value={reportName} OnChange={(e: any) => setReportName(e.target.value)} />

                <Grid item xs={12}>
                  <b>Date Range</b>
                </Grid>
                <Dropdown Size={6} Variant="standard" NoLabel Options={dateRangeOptions.map(o => ({ Id: o.id, Value: o.label }))} Value={dateRangeType} OnChange={(v: any) => setDateRangeType(v)} />
                {(dateRangeType === 'RollingDays' || dateRangeType === 'RollingMonths') ? (
                  <Dropdown Size={6} Variant="standard" NoLabel Options={(dateRangeType === 'RollingDays' ? rollingDaysValues : rollingMonthsValues).map(n => ({ Id: n, Value: n.toString() }))} Value={dateRangeValue ?? ''} OnChange={(v: any) => setDateRangeValue(parseInt(v))} />
                ) : <Grid item xs={6} />}
                <DateInput Borderless Size={6} Label="From" Value={dateRangeType === 'Custom' ? startDate : effectiveStart} Disabled={dateRangeType !== 'Custom'} OnChange={(v: any) => setStartDate(new DateTime(v))} />
                <DateInput Borderless Size={6} Label="To" Value={dateRangeType === 'Custom' ? endDate : effectiveEnd} Disabled={dateRangeType !== 'Custom'} OnChange={(v: any) => setEndDate(new DateTime(v))} />

                <Grid item xs={12} sx={{ marginTop: 2 }}>
                  <b>Table Setup</b>
                </Grid>
                <Dropdown Size={12} Label="Group By" NoMB Options={[
                  { Id: "", Value: "None" },
                  ...["Date", "User", "Merchant", "Budget", "SubBudget"].map(v => ({ Id: v, Value: GetReportLabel(v) })),
                  ...bucketColumnKeys.map(v => ({ Id: v, Value: GetReportLabel(v) }))
                ]} Value={grouping} OnChange={(v: any) => setGrouping(v)} />

                <Grid item xs={12}>
                  <div className="mt-3">Columns</div>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={columnOrder} strategy={verticalListSortingStrategy}>
                      {columnOrder.map((col: string) => {
                        const enabled = columns.includes(col);
                        return (
                          <SortableRow key={col} id={col} enabled={enabled}>
                            <span className="customReport-handle bi bi-list" aria-label="drag handle" />
                            <div className="customReport-flex-grow">{GetReportLabel(col)}</div>
                            <Checkbox size="small" checked={enabled} onChange={(e) => toggleColumnEnabled(col, e.target.checked)} />
                          </SortableRow>
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                </Grid>

                <Grid item xs={12} sx={{ marginTop: 2 }}>
                  <b>Filters</b>
                </Grid>
                <Dropdown Size={12} Label={budgetPluralLabel} NoMB Multiple Options={(budgets||[]).map(b => ({ Id: b.budgetId, Value: b.description }) as SelectableItem)} Value={selectedBudgets} OnChange={(vals: number[]) => setSelectedBudgets(vals)} />
                {subBudgets.length > 0 && <Dropdown Size={12} Label={subBudgetPluralLabel} NoMB Multiple Options={(subBudgets||[]).map(b => ({ Id: b.subBudgetId, Value: b.description }) as SelectableItem)} Value={selectedSubBudgets} OnChange={(vals: number[]) => setSelectedSubBudgets(vals)} />}
                {bucketDefinitionsForReports.map(def => {
                  const typeKey = def.bucketTypeKey;
                  const options = (bucketNodesByType[typeKey] ?? []).map(n => ({ Id: n.id, Value: n.name }) as SelectableItem);
                  const selected = selectedBucketNodeIds[typeKey] ?? [];
                  return (
                    <Dropdown
                      key={typeKey}
                      Size={12}
                      Label={FamilyBucketPluralLabel(user, typeKey, def.pluralLabel || def.label || typeKey)}
                      NoMB
                      Multiple
                      Options={options}
                      Value={selected}
                      OnChange={(vals: number[]) => setSelectedBucketNodeIds(prev => {
                        const next = { ...prev };
                        if (!vals || vals.length === 0) delete next[typeKey];
                        else next[typeKey] = vals;
                        return next;
                      })}
                    />
                  );
                })}
                {(user.isHoH || users.length !== 1) && <Dropdown Size={12} Label="Users" NoMB Multiple Options={(users||[]).map(u => ({ Id: u.userAccountId, Value: `${u.firstName} ${u.lastName}` }) as SelectableItem)} Value={selectedUsers} OnChange={(vals: number[]) => setSelectedUsers(vals)} />}

                <Toggle Size={6} Label="Include Spending" OnChange={(e: any) => setIncludeSpending(e.target.checked)} Checked={includeSpending} />
                <Toggle Size={6} Label="Include Miles" OnChange={(e: any) => setIncludeMiles(e.target.checked)} Checked={includeMiles} />
                <Toggle Size={6} Label="Include Checks" OnChange={(e: any) => setIncludeCheckRequests(e.target.checked)} Checked={includeCheckRequests} />
                <Toggle Size={6} Label={`Combine Split ${budgetPluralLabel}`} OnChange={(e: any) => setCombineSplitBudgets(e.target.checked)} Checked={combineSplitBudgets} />
                <Toggle Size={6} Label="Include Receipts" OnChange={(e: any) => setIncludeReceipts(e.target.checked)} Checked={includeReceipts} />
                <Toggle Size={6} Label="Group Receipts" OnChange={(e: any) => setGroupReceipts(e.target.checked)} Checked={groupReceipts} />
              </Grid>
            </Box>

            <Box sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper', pt: 1, pb: 1, zIndex: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, paddingTop: 2 }}>
                <Button variant="outlined" color="secondary" onClick={handleSave}>Save</Button>
                {reportId && (
                  <>
                    <Button variant="outlined" color="info" onClick={handleSaveAsNew}>Save As New</Button>
                    <Button variant="outlined" color="error" onClick={handleDelete}>Delete</Button>
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={overlay}>
        <CircularProgress />
      </Backdrop>
    </div>
  );

  async function handleDownload() {
    setOverlay(true);
    try {
      const settings = buildSettings();

      const file = await repo.GetReportPdfAsync(settings);
      const sanitizeFileName = (name: string) => {
        // Remove invalid filename characters and trim whitespace
        const cleaned = name.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim();
        // Windows does not allow trailing dots or spaces in file names
        return cleaned.replace(/[.\s]+$/g, "") || "Custom Report";
      };
      const preferredName = (reportName && reportName.trim().length > 0) ? reportName : settings.title;
      const fileName = sanitizeFileName(preferredName);
      DownloadPdf(await file.blob() as Blob, fileName);
    } finally {
      setOverlay(false);
    }
  }

  function buildSettings(): CustomReportSettings {
    const filter: CustomReportFilter = {
      startDate: effectiveStart,
      endDate: effectiveEnd,
      budgetIds: selectedBudgets.length ? selectedBudgets : undefined,
      subBudgetIds: selectedSubBudgets.length ? selectedSubBudgets : undefined,
      bucketNodeIds: Object.keys(selectedBucketNodeIds).length ? selectedBucketNodeIds : undefined,
      createdById: selectedUsers.length ? selectedUsers : undefined,
      includeSpending,
      includeMiles,
      includeCheckRequests,
      combineSplitBudgets,
      includeReceipts,
      groupReceipts
    } as CustomReportFilter;

    const settings: CustomReportSettings = {
      title: reportName || defaultReportName,
      columns: columns,
      columnLabels: BuildReportColumnLabels(user, columns),
      grouping: grouping,
      filter
    } as CustomReportSettings;
    return settings;
  }

  function handleSave() {
    const name = (reportName && reportName.trim().length > 0) ? reportName.trim() : defaultReportName;
    // For saved reports, do not persist absolute dates unless custom
    const saveSettings = buildSettings();
    if (dateRangeType !== 'Custom') {
      if (saveSettings.filter) {
        delete (saveSettings.filter as any).startDate;
        delete (saveSettings.filter as any).endDate;
      }
    }
    const relativeRange: RelativeDateRange | undefined = dateRangeType === 'Custom' ? { type: 'Custom' } : { type: dateRangeType, value: dateRangeValue };
    if (reportId) {
      dispatch(updateSavedReport({ id: reportId, name, settings: saveSettings, relativeRange }));
    } else {
      dispatch(addSavedReport({ name, settings: saveSettings, relativeRange }));
    }
    // After saving, navigate to Reports to see it listed
    navigate('/reports');
  }

  function handleSaveAsNew() {
    const name = (reportName && reportName.trim().length > 0) ? reportName.trim() : defaultReportName;
    const saveSettings = buildSettings();
    if (dateRangeType !== 'Custom') {
      if (saveSettings.filter) {
        delete (saveSettings.filter as any).startDate;
        delete (saveSettings.filter as any).endDate;
      }
    }
    const relativeRange: RelativeDateRange | undefined = dateRangeType === 'Custom' ? { type: 'Custom' } : { type: dateRangeType, value: dateRangeValue };
    dispatch(addSavedReport({ name, settings: saveSettings, relativeRange }));
    navigate('/reports');
  }

  function handleDelete() {
    if (!reportId) return;
    const ok = window.confirm('Delete this saved report? This cannot be undone.');
    if (!ok) return;
    dispatch(deleteSavedReport(reportId));
    navigate('/reports');
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = columnOrder.indexOf(active.id as string);
    const newIndex = columnOrder.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(columnOrder, oldIndex, newIndex);
    setColumnOrder(next);
    setColumns(prev => [...prev].sort((a, b) => next.indexOf(a) - next.indexOf(b)));
  }

  function toggleColumnEnabled(col: string, enabled: boolean) {
    if (enabled) {
      if (columns.includes(col)) return;
      const added = [...columns, col];
      added.sort((a, b) => columnOrder.indexOf(a) - columnOrder.indexOf(b));
      setColumns(added);
    } else {
      if (!columns.includes(col)) return;
      setColumns(columns.filter(c => c !== col));
    }
  }

	function handleClose() {
		navigate('/reports');
	}
};

export default CustomReport;

// Sortable row component for DnD Kit
const SortableRow = (props: { id: string; enabled: boolean; children: React.ReactNode }) => {
  const { id, enabled, children } = props as any;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id });

  return (
    <Box ref={setNodeRef} sx={{ transform: CSS.Transform.toString(transform), transition }} className={`customReport-order-row${enabled ? '' : ' disabled'}`}>
      {/* Attach drag listeners to the handle (first child expected to be handle span) */}
      {React.Children.map(children, (child: any, idx: number) => {
        if (idx === 0 && React.isValidElement(child)) {
          return React.cloneElement(child, { ...attributes, ...listeners });
        }
        return child;
      })}
    </Box>
  );
};

// ---- Date range helpers ----
const dateRangeOptions: { id: RelativeDateRangeType; label: string }[] = [
  { id: 'ThisMonth', label: 'This Month' },
  { id: 'LastMonth', label: 'Last Month' },
  { id: 'Last7Days', label: 'Last 7 Days' },
  { id: 'Last30Days', label: 'Last 30 Days' },
  { id: 'ThisYear', label: 'This Year' },
  { id: 'LastYear', label: 'Last Year' },
  { id: 'RollingDays', label: 'Rolling Days' },
  { id: 'RollingMonths', label: 'Rolling Months' },
  { id: 'Custom', label: 'Custom' },
];

const rollingDaysValues = [7, 14, 30, 60, 90, 180, 365];
const rollingMonthsValues = [1, 3, 6, 12];

function computeDateRange(type: RelativeDateRangeType, value: number | undefined, now: DateTime) {
  const n = value ?? 30;
  switch (type) {
    case 'ThisMonth':
      return { start: now.startOfMonth(), end: now };
    case 'LastMonth': {
      const start = new DateTime(dayjs(now.Value).subtract(1, 'month').startOf('month').toDate());
      const end = new DateTime(dayjs(now.Value).subtract(1, 'month').endOf('month').toDate());
      return { start, end };
    }
    case 'Last7Days':
      return { start: now.addDays(-7), end: now };
    case 'Last30Days':
      return { start: now.addDays(-30), end: now };
    case 'LastYear': {
      const start = new DateTime(dayjs(now.Value).subtract(1, 'year').startOf('year').toDate());
      const end = new DateTime(dayjs(now.Value).subtract(1, 'year').endOf('year').toDate());
      return { start, end };
    }
    case 'ThisYear': {
      const start = now.startOfYear();
      const end = new DateTime(dayjs(now.Value).endOf('year').toDate());
      return { start, end };
    }
    case 'RollingDays':
      return { start: now.addDays(-n), end: now };
    case 'RollingMonths':
      return { start: now.addMonths(-n), end: now };
    case 'Custom':
    default:
      return { start: now.startOfMonth(), end: now };
  }
}
